import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Shield, Mail, Lock, Loader2,
  Brain, Sparkles, FileCheck, AlertTriangle, Clock, CalendarDays, Languages,
} from 'lucide-react';
import { toast } from 'sonner';

const HERO_FEATURES = [
  { icon: Brain, title: '13 módulos EU AI Act (F1–F13)', desc: 'Inventário, Wizard de Classificação, Doc Técnica Anexo IV, Transparência, Supervisão Humana, GPAI, Audit Trail, Post-Market.' },
  { icon: Shield, title: 'Gestão NIS2 completa', desc: 'Ativos, 25 Requisitos, Incidentes 24h/72h, Vulnerabilidades com webhooks, Riscos, Aprovações e Relatórios PDF.' },
  { icon: Sparkles, title: 'AI Compliance Co-pilot + Health Score', desc: 'Chatbot regulatório sempre visível e Health Score 0–100 com Top 3 ações sugeridas pela IA.' },
  { icon: FileCheck, title: 'Auto-Drafter de Documentação Técnica', desc: 'Reduz ~85% do tempo de redação do Anexo IV (Art. 11) e da Ficha de Transparência (Art. 13).' },
  { icon: CalendarDays, title: 'Compliance Roadmap Generator 90 dias', desc: 'Gera plano semanal W1–W13 e materializa tarefas reais com progress bar por semana.' },
  { icon: AlertTriangle, title: 'Notificações regulatórias automáticas', desc: 'Timers 24h/72h (NIS2) + 15 dias (Art. 73 AI Act) com email transacional Resend.' },
  { icon: Languages, title: '100% Português europeu', desc: 'Vocabulário regulatório pt-PT e referências explícitas a cada artigo da legislação.' },
];

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Impersonation magic-link handler (para o Hub abrir sessão de suporte)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('impersonation_token');
    if (!token) return;
    try {
      localStorage.setItem('auth_token', token);
      const parts = token.split('.');
      const payload = parts.length === 3 ? JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) : {};
      toast.success(`Sessão de suporte iniciada como ${payload.email || 'utilizador'} (expira ${new Date(payload.exp * 1000).toLocaleString('pt-PT')})`);
      // limpa o token do URL antes de navegar
      window.history.replaceState({}, '', '/dashboard');
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error('Magic link inválido ou expirado');
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Sessão iniciada com sucesso');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar sessão');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1760124056883-732e7a5e2e68?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        data-testid="login-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/95 via-slate-900/90 to-purple-950/95" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white w-full overflow-y-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <span className="text-3xl font-bold font-heading tracking-tight">NIS2AI</span>
              <p className="text-[11px] text-indigo-200 uppercase tracking-widest mt-0.5">NIS2 · EU AI Act</p>
            </div>
          </div>

          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-indigo-200 bg-indigo-500/20 border border-indigo-400/30 rounded-full px-2.5 py-1">
              <Sparkles className="h-3 w-3" /> Conformidade com IA generativa
            </span>
          </div>
          <h1 className="text-4xl font-bold mb-3 font-heading leading-tight">
            Conformidade <span className="text-indigo-300">NIS2</span> &amp; <span className="text-purple-300">EU AI Act</span> numa só plataforma
          </h1>
          <p className="text-base text-slate-300 max-w-xl leading-relaxed">
            A primeira plataforma ibérica que unifica a Diretiva (UE) 2022/2555 (NIS2 + Lei 125/2025) e o Regulamento (UE) 2024/1689 (EU AI Act),
            com automação por <strong className="text-white">Motor de IA</strong> nos pontos críticos.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-2.5 max-w-xl">
            {HERO_FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-3 group" data-testid={`hero-feature-${i}`}>
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-white/5 border border-white/10 shrink-0 group-hover:bg-indigo-500/20 group-hover:border-indigo-400/30 transition-colors">
                  <f.icon className="h-4 w-4 text-indigo-300" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{f.title}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-3 flex-wrap text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
              <Clock className="h-3 w-3" /> Multi-tenant
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">Motor de IA</span>
            <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">Resend · Webhooks Nessus/Qualys</span>
          </div>
        </div>
      </div>

      {/* Right side - Login form (no register) */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-2xl font-bold font-heading text-slate-900">NIS2AI</span>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest">NIS2 · EU AI Act</p>
            </div>
          </div>

          <Card className="border-slate-200" data-testid="login-card">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading">Bem-vindo de volta</CardTitle>
              <CardDescription>Introduza as suas credenciais para aceder à plataforma</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="email@empresa.pt"
                      className="pl-10"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      autoComplete="email"
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      autoComplete="current-password"
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Entrar
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Ou continuar com</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Google
              </Button>

              <div className="mt-6 pt-4 border-t border-slate-100 text-center" data-testid="login-access-note">
                <p className="text-xs text-slate-500 leading-relaxed">
                  O acesso é <strong>apenas por convite</strong>. Contas são criadas
                  pela administração da plataforma.
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Sem acesso? Contacte o administrador da sua organização.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
